import { MAIN_FRAME_ID, Rect, WindowOptions } from "./NativeDesktopData";
import { NativeDesktop } from "./NativeDesktopSupportShared";

export class NativeDesktopWindow {
  protected desktop: NativeDesktop = null;

  protected attachedElement: Element = null;

  private _onClose: (NativeDesktopWindow) => boolean = null;

  private _originalBounds: { x: number; y: number; w: number; h: number } = null;
  private restoreListeners: Array<any> = [];

  constructor(protected nativeWindow: Window) {
    this.desktop = (window as any).desktop;
    if (this.desktop == null && window.opener?.desktop != null) {
      this.desktop = window.opener.desktop;
    }

    if (nativeWindow != null) {
      nativeWindow.close = this.close.bind(this);
    }
  }

  public getId(): string {
    return this.nativeWindow.document.body.getAttribute("frameId");
  }

  public isMainWindow(): boolean {
    return this.getId() == null;
  }

  public getNativeWindow(): Window {
    return this.nativeWindow;
  }

  public setBounds(x: number, y: number, width: number, height: number): Promise<void> {
    let bounds: Rect = { left: x, top: y, right: x + width, bottom: y + height };
    return this.desktop._window.setBounds(this.getId(), bounds);
  }

  public setMinimumSize(width: number, height: number): Promise<void> {
    return this.desktop._window.setMinimumSize(this.getId(), width, height);
  }

  public getOptions(): Promise<WindowOptions> {
    return this.desktop._window.getOptions(this.getId());
  }

  public updateOptions(options: WindowOptions): Promise<void> {
    return this.desktop._window.updateOptions(this.getId(), options);
  }

  public show(inactive: boolean = false): Promise<void> {
    this.processAttachedElementOnShow();
    return this.desktop._window.show(this.getId(), inactive);
  }

  public showAt(x: number, y: number, inactive: boolean = false): Promise<void> {
    this.processAttachedElementOnShow();
    return this.desktop._window.showAt(this.getId(), x, y, inactive);
  }

  protected processAttachedElementOnShow(): void {
    if (this.attachedElement != null) {
      let parentId = this.attachedElement?.ownerDocument?.body?.getAttribute("frameId");
      if (parentId != null && parentId != this.getId() && parentId != MAIN_FRAME_ID) {
        this.desktop._window.setParentWindow(this.getId(), parentId, true);
      }
    }
  }

  public hide(): Promise<void> {
    if (this.nativeWindow.document.visibilityState === "hidden") {
      return new Promise<void>((resolve) => {
        resolve();
      });
    }

    return new Promise<void>((resolve) => {
      this.desktop._window.hide(this.getId()).then(() => {
        if (this.attachedElement != null) {
          this.desktop._window.setParentWindow(this.getId(), null, true).then(() => {
            resolve();
          });
        } else {
          resolve();
        }
      });
    });
  }

  public minimize(): Promise<void> {
    return this.desktop._window.minimize(this.getId());
  }

  /**
   *
   * @param maximizeWorkaround if this flag is true than maximize
   * will store the position and size of the window and try to restore
   * using that size with restore to avoid an issue with electron and isMaximized
   * returning false on transparent windows with large scale set on the display (> 150%)
   *
   * @returns
   */
  public maximize(maximizeWorkaround: boolean = false): Promise<void> {
    if (maximizeWorkaround) {
      this._originalBounds = {
        x: this.nativeWindow.screenX,
        y: this.nativeWindow.screenY,
        w: this.nativeWindow.innerWidth,
        h: this.nativeWindow.innerHeight,
      };
    }

    return this.desktop._window.maximize(this.getId());
  }

  /**
   *
   * @param maximizeWorkaround if this flag is true than maximize
   * will store the position and size of the window and try to restore
   * using that size with restore to avoid an issue with electron and isMaximized
   * returning false on transparent windows with large scale set on the display (> 150%)
   *
   * @returns
   */
  public restore(maximizeWorkaround: boolean = false): Promise<void> {
    let bounds = this._originalBounds;

    this._originalBounds = null;

    if (maximizeWorkaround && bounds != null) {
      return this.setBounds(bounds.x, bounds.y, bounds.w, bounds.h);
    }

    return this.desktop._window.restore(this.getId());
  }

  public setAsForeground(focus?: boolean): Promise<void> {
    return this.desktop._window.setAsForeground(this.getId(), focus);
  }

  public restoreToFront(): Promise<void> {
    return this.desktop._window.restoreToFront(this.getId());
  }

  public focus(): Promise<void> {
    return this.desktop._window.setAsForeground(this.getId());
  }

  public isMinimized(): Promise<boolean> {
    return this.desktop._window.isMinimized(this.getId());
  }

  public isMaximized(): Promise<boolean> {
    return this.desktop._window.isMaximized(this.getId());
  }

  public getState(): Promise<string> {
    return this.desktop._window.getState(this.getId());
  }

  public flash(): Promise<void> {
    return this.desktop._window.flash(this.getId(), true);
  }

  public stopFlashing(): Promise<void> {
    return this.desktop._window.flash(this.getId(), false);
  }

  public setSize(width: number, height: number): Promise<void> {
    return this.desktop._window.setSize(this.getId(), width, height);
  }

  public setParentWindow(parentWindow: NativeDesktopWindow | null): Promise<void> {
    let parentId = parentWindow != null ? parentWindow.getId() : null;
    return this.desktop._window.setParentWindow(this.getId(), parentId, false);
  }

  public addEventListener(event: string, callback: (event) => void) {
    this.desktop.on(event, (frameId, event) => {
      if (frameId === this.getId()) {
        callback(event);
      }
    });

    if (event === "restored" && !this.restoreListeners.includes(callback)) {
      this.restoreListeners.push(callback);
    }
  }

  public close(): void {
    if (this.onClose != null) {
      this.preventClosing(false).then(() => {
        this.desktop._window.close(this.getId());
      });
    } else {
      this.desktop._window.close(this.getId());
    }
  }

  public openDevTools(): void {
    this.desktop._window.openDevTools(this.getId());
  }

  public titleElement(): Element {
    return this.getNativeWindow()?.document?.head?.querySelector("title");
  }

  /**
   * Specific behaviour where this window will set the owner window of
   * the element provider as it's parent window when shown, and remove it when
   * hidden.
   * @param element the element used to derive this window's parent
   */
  public attachToParentElement(element: Element): void {
    this.attachedElement = element;
  }

  public detachFromParentElement(): void {
    this.attachedElement = null;
  }

  public preventClosing(preventClosing: boolean): Promise<void> {
    return this.desktop._window.preventClosing(this.getId(), preventClosing);
  }

  public get onClose(): (NativeDesktopWindow) => boolean {
    return this._onClose;
  }

  public set onClose(callback: (NativeDesktopWindow) => boolean) {
    this.preventClosing(callback != null);
    this._onClose = callback;
  }

  public hideSmallWindows(): Promise<void> {
    return this.desktop._window.hideSmallWindows(this.getId());
  }

  public fireRestoreEvent(event): void {
    if (this.restoreListeners.length > 0) {
      this.restoreListeners.forEach((callback) => callback(event));
    }
  }
}
